import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  Download,
  HelpCircle,
  Loader2,
  Phone,
  PhoneCall,
  RotateCw,
  Upload,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MetricCards } from "@/components/metric-cards";
import { Input } from "@/components/ui/input";
import { useDashboardAnalyticsBundle, useDashboardStats } from "@/hooks/use-dashboard";
import { cn } from "@/utils";
import { UploadModal } from "@/components/upload-modal";
import { DateRangePicker } from "@/components/date-range-picker";
import { FailReasonChart } from "@/components/fail-reason-chart";
import { TransitionRetryChart } from "@/components/transition-retry-chart";
import { VapiAssistantChart } from "@/components/vapi-assistant-chart";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { fetchCasesForDashboardExport } from "@/lib/dashboard-export";
import { exportToCSV, exportToXLS } from "@/lib/export";
import type { DashboardStatsQuery } from "@/types";

function readParam(
  searchParams: URLSearchParams,
  key: string,
): string | undefined {
  const v = searchParams.get(key);
  return v && v.trim() ? v.trim() : undefined;
}

export default function DashboardPage() {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [chartRefetch, setChartRefetch] = useState(0);
  const [transitionFromStatuses, setTransitionFromStatuses] = useState<string[]>(
    [],
  );
  const [transitionToStatuses, setTransitionToStatuses] = useState<string[]>([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const ignoreNextUrlSync = useRef(false);
  const [productInput, setProductInput] = useState(
    () => searchParams.get("product") ?? "",
  );

  const statsQuery = useMemo((): DashboardStatsQuery => {
    return {
      product: readParam(searchParams, "product"),
      bin: readParam(searchParams, "bin"),
      pcn: readParam(searchParams, "pcn"),
      from: readParam(searchParams, "from"),
      to: readParam(searchParams, "to"),
    };
  }, [searchParams]);

  const onPathFilterParamsChange = useCallback(
    (fromStatuses: string[], toStatuses: string[]) => {
      setTransitionFromStatuses(fromStatuses);
      setTransitionToStatuses(toStatuses);
    },
    [],
  );

  const {
    stats,
    loading: statsLoading,
    error: statsError,
    refresh: refreshStats,
  } = useDashboardStats(statsQuery);

  const {
    transition,
    failReasonBreakdown,
    vapiAssistant,
    loading: bundleLoading,
    error: bundleError,
    refresh: refreshBundle,
  } = useDashboardAnalyticsBundle(
    statsQuery,
    transitionFromStatuses,
    transitionToStatuses,
    chartRefetch,
  );

  const refresh = useCallback(() => {
    void refreshStats();
    void refreshBundle();
  }, [refreshStats, refreshBundle]);

  const loading = statsLoading || bundleLoading;
  const error = statsError ?? bundleError;

  useEffect(() => {
    if (ignoreNextUrlSync.current) {
      ignoreNextUrlSync.current = false;
      return;
    }
    setProductInput(searchParams.get("product") ?? "");
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = productInput.trim();
      const cur = (searchParams.get("product") ?? "").trim();
      if (next === cur) return;
      ignoreNextUrlSync.current = true;
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set("product", next);
          else p.delete("product");
          return p;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [productInput, searchParams, setSearchParams]);

  const setSingleParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const v = value.trim();
          if (v) p.set(key, v);
          else p.delete(key);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onDateRangeChange = useCallback(
    (next: { from?: string; to?: string }) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next.from?.trim()) p.set("from", next.from.trim());
          else p.delete("from");
          if (next.to?.trim()) p.set("to", next.to.trim());
          else p.delete("to");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleExportCsv = useCallback(async () => {
    setExportBusy(true);
    try {
      const { cases, truncated } = await fetchCasesForDashboardExport(statsQuery);
      const base = `redi-dashboard-export-${new Date().toISOString().slice(0, 10)}`;
      exportToCSV(cases, `${base}.csv`);
      if (truncated) {
        window.alert(
          "This export reached the maximum of 50,000 rows. Add filters (for example product name or date range) or use Case Records export if you need a different slice.",
        );
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExportBusy(false);
    }
  }, [statsQuery]);

  const handleExportXls = useCallback(async () => {
    setExportBusy(true);
    try {
      const { cases, truncated } = await fetchCasesForDashboardExport(statsQuery);
      const base = `redi-dashboard-export-${new Date().toISOString().slice(0, 10)}`;
      exportToXLS(cases, `${base}.xlsx`);
      if (truncated) {
        window.alert(
          "This export reached the maximum of 50,000 rows. Add filters (for example product name or date range) or use Case Records export if you need a different slice.",
        );
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExportBusy(false);
    }
  }, [statsQuery]);

  const metrics = useMemo(
    () => [
      { label: "Total", value: stats?.total ?? 0, icon: Phone, color: "text-blue-500" },
      { label: "Successful", value: stats?.successful ?? 0, icon: CheckCircle, color: "text-green-500" },
      {
        label: "Failed",
        value: stats?.failed ?? 0,
        valueCaption: "retryable",
        secondary: { value: stats?.failed_locked ?? 0, label: "locked" },
        icon: XCircle,
        color: "text-red-500",
      },
      { label: "Active", value: stats?.active ?? 0, icon: PhoneCall, color: "text-yellow-500" },
      { label: "Pending", value: stats?.pending ?? 0, icon: Clock, color: "text-purple-500" },
      {
        label: "Other",
        value: stats?.other ?? 0,
        icon: HelpCircle,
        color: "text-slate-500",
      },
    ],
    [stats],
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Case statistics. Filters apply to all statistics.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExportCsv()}
            disabled={exportBusy}
            className="gap-1"
            aria-busy={exportBusy}
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExportXls()}
            disabled={exportBusy}
            className="gap-1"
            aria-busy={exportBusy}
          >
            <Download className="h-3.5 w-3.5" />
            Excel
          </Button>
          <Button variant="outline" onClick={() => setUploadModalOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload CSV
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              void refresh();
              setChartRefetch((k) => k + 1);
            }}
            disabled={loading}
            aria-label="Refresh stats"
          >
            <RotateCw
              className={cn("h-4 w-4", loading && "animate-spin")}
            />
          </Button>
        </div>
      </div>

      <section
        className="grid gap-4 rounded-lg border border-border bg-card p-4"
        aria-label="Statistics filters"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div className="space-y-1.5 sm:col-span-2">
            <label
              htmlFor="dash-product"
              className="text-sm font-medium leading-none"
            >
              Product name starts with
            </label>
            <Input
              id="dash-product"
              value={productInput}
              onChange={(e) => setProductInput(e.target.value)}
              placeholder="e.g. Lipitor"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Debounced, case-insensitive.</p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="dash-bin" className="text-sm font-medium leading-none">
              Member BIN
            </label>
            <Input
              id="dash-bin"
              value={searchParams.get("bin") ?? ""}
              onChange={(e) => setSingleParam("bin", e.target.value)}
              placeholder="Exact"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="dash-pcn" className="text-sm font-medium leading-none">
              Member PCN
            </label>
            <Input
              id="dash-pcn"
              value={searchParams.get("pcn") ?? ""}
              onChange={(e) => setSingleParam("pcn", e.target.value)}
              placeholder="Exact"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <span className="block text-sm font-medium leading-none">Created at</span>
            <DateRangePicker
              from={statsQuery.from}
              to={statsQuery.to}
              onChange={onDateRangeChange}
            />
          </div>
        </div>
        {loading ? (
          <div
            className="flex items-center gap-2 rounded-md border border-dashed border-border/80 bg-muted/20 px-3 py-2 text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
            aria-label="Loading dashboard data"
          >
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
              aria-hidden
            />
            <span>Updating statistics and charts…</span>
          </div>
        ) : null}
      </section>

      <UploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUploaded={() => {
          void refresh();
          setChartRefetch((k) => k + 1);
        }}
      />

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <MetricCards metrics={metrics} loading={statsLoading} />

      <TransitionRetryChart
        statsQuery={statsQuery}
        refetchKey={chartRefetch}
        bundleTransition={transition}
        bundleLoading={bundleLoading}
        onPathFilterParamsChange={onPathFilterParamsChange}
        onBundleRefresh={refresh}
      />

      <FailReasonChart
        statsQuery={statsQuery}
        refetchKey={chartRefetch}
        breakdownFromBundle={failReasonBreakdown}
        bundleLoading={bundleLoading}
        onBundleRefresh={refresh}
      />

      <VapiAssistantChart
        statsQuery={statsQuery}
        refetchKey={chartRefetch}
        vapiFromBundle={vapiAssistant}
        bundleLoading={bundleLoading}
        onBundleRefresh={refresh}
      />
    </div>
  );
}
